import { faPrint } from "@fortawesome/free-solid-svg-icons"
import React, { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { ButtonIcon } from "../../../components/Button/Button"
import { CanView } from "../../../components/CanView/CanView"
import { OrderPrintOptionsPupop } from "../../../components/OrderPrintOptionsPupop/OrderPrintOptionsPupop"
import { SIZE } from "../../../configuration/Sizes"
import THEME from "../../../configuration/Theme"
import { useConfirmation } from "../../../context/useConfirmationContext"
import { EDeliveryStatusType } from "../../../Enum/EDeliveryStatusType"
import { EOrderCurrentStatus } from "../../../Enum/EOrderCurrentStatus"
import { EOrderPlaceType } from "../../../Enum/EOrderPlaceType"
import { EOrderStatusType } from "../../../Enum/EOrderStatus"
import { Permissions } from "../../../Enum/PermissionsType"
import { useSaveOrder } from "../../../hooks/Order/useSaveOrder"
import { OrderEntity } from "../../../Objects/Order/OrderEntity"
import { FulfillmentStation, getFulfillmentStationItems } from "../fulfillmentOrderCounters"
import { EFulfillmentMode } from "../../../Enum/EFulfillmentMode"
import { FulfillmentItem } from "./FulfillmentItem"
import { RenderDeliveryDetails } from "./RenderDeliveryDetails"

interface FulfillmentOrderCardProps {
  order: OrderEntity
  type: EOrderStatusType | "ALL"
  station: FulfillmentStation
  mode: EFulfillmentMode
  onUpdate: () => void
  onUpdateStatus: (id: number, status: EOrderStatusType) => void
  onSelectCourier: (order: OrderEntity) => void
  onCancelSearch: (deliveryId: number) => void
}

export const FulfillmentOrderCard: React.FC<FulfillmentOrderCardProps> = ({
  order,
  type,
  station,
  mode,
  onUpdate,
  onUpdateStatus,
  onSelectCourier,
  onCancelSearch,
}) => {
  const { t } = useTranslation("fulfillment")

  const { setNewConfirmation } = useConfirmation()

  const { handleSaveItemOrderStatus } = useSaveOrder()

  const [isPrintOpen, setIsPrintOpen] = useState(false)

  const stationItems = useMemo(() => getFulfillmentStationItems(order, station, mode), [order, station, mode])

  const allOrderItems = useMemo(() => order.getAllCatalogProducts(), [order])

  const isDelivery = order.eOrderPlaceType === EOrderPlaceType.Delivery && !order.isStorePickup

  const allStationItemsComplete = stationItems.every(
    (item) => item.currentStatus === EOrderCurrentStatus.Finished || item.currentStatus === EOrderCurrentStatus.Closed,
  )

  const allOrderItemsComplete = allOrderItems.every(
    (item) => item.currentStatus === EOrderCurrentStatus.Finished || item.currentStatus === EOrderCurrentStatus.Closed,
  )

  const delivery = order.deliveryEntity

  const canShowActionButtons =
    !delivery ||
    delivery.status === EDeliveryStatusType.Canceled ||
    delivery.status === EDeliveryStatusType.Expired ||
    delivery.status === EDeliveryStatusType.Rejected

  const visibleItems = stationItems.filter(
    (item) => type === "ALL" || item.currentStatus === type || item.currentStatus === EOrderCurrentStatus.Closed,
  )

  const servicePointName = order.servicePointEntity?.name?.trim() ?? ""

  const updateItem = (itemId: number, isNextStep: boolean) =>
    handleSaveItemOrderStatus(order.id, itemId, isNextStep, onUpdate)

  const markDelivered = () =>
    setNewConfirmation(t("mark_delivered_title"), t("mark_delivered_message"), () =>
      onUpdateStatus(order.id, EOrderStatusType.Delivered),
    )

  const statusClass =
    order.eOrderStatusType === EOrderStatusType.Delivered
      ? "bg-green-100"
      : order.eOrderStatusType === EOrderStatusType.InDelivery
        ? "bg-orange-200/70"
        : order.eOrderStatusType === EOrderStatusType.PreparingFinished
          ? "bg-purple-200/70"
          : order.eOrderStatusType === EOrderStatusType.Preparing
            ? "bg-amber-200/70"
            : "bg-blue-50"

  return (
    <Animate type={AnimationTypes.slideRightIn}>
      <>
        <article className={`flex flex-col rounded-2xl shadow-md ring-2 ring-slate-200 ${statusClass}`}>
          <header className="rounded-t-2xl border-b bg-gray-50/80 p-4">
            <div className="flex items-start justify-between">
              <div>
                <span className="text-xs font-bold uppercase text-slate-500">
                  {servicePointName
                    ? t("service_point", { number: servicePointName })
                    : order.isStorePickup
                      ? t("pickup_order_title")
                      : t("order_online")}
                </span>

                <h3 className="text-lg font-black text-red-500">
                  {order.isStorePickup ? t("pickup_order_label") : order.eOrderPlaceType.toOrderPlaceTypeString()} #{order.id}
                </h3>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-slate-500">{order.date.toReadableDate()}</span>
                <ButtonIcon
                  typeButton="button"
                  sizeNumber={SIZE.buttonExpandIcon}
                  bgColor={THEME.colors.buttonBGCloseColor}
                  iconColor={THEME.colors.buttonFGCloseColor}
                  icon={faPrint}
                  onClick={() => setIsPrintOpen(true)}
                  size="small"

                  descriptionKey="interactionDescriptions:pages.fulfillmentPage.components.fulfillmentOrderCard.button.setIsPrintOpenTrue" />
              </div>
            </div>

            <div className="mt-2 text-sm font-semibold text-slate-600">{order.customerEntity?.personEntity?.name}</div>

            <span
              className={`mt-2 inline-flex rounded-full px-3 py-1 text-[11px] font-black uppercase ${
                order.orderPaymentEntity?.isClosed()
                  ? "bg-emerald-100 text-emerald-700"
                  : "bg-amber-100 text-amber-700"
              }`}
            >
              {order.orderPaymentEntity?.isClosed() ? t("paid") : t("payment_pending")}
            </span>
          </header>

          <div className="flex-1 space-y-3 rounded-b-2xl bg-white p-2">
            {visibleItems.length > 0 && (
              <div>
                <h4 className="mb-3 text-xs font-bold uppercase tracking-wider text-slate-400">{t("items")}</h4>
                {visibleItems.map((item) => (
                  <FulfillmentItem
                    key={item.id}
                    item={item}
                    isDelivery={isDelivery}
                    canShowActionButtons={canShowActionButtons}
                    onUpdateStatus={updateItem}
                  />
                ))}
              </div>
            )}

            <CanView permission={Permissions.CanEditeFulfillment}>
              {!order.isFinished() && allStationItemsComplete && allOrderItemsComplete && (
                <div className="space-y-2 p-1">
                  {(!isDelivery || canShowActionButtons) && (
                    <button onClick={markDelivered} className="w-full rounded-lg bg-emerald-200 py-3 font-bold text-gray-800 ring-2 ring-emerald-600 hover:bg-emerald-500" data-interaction-description-key="interactionDescriptions:pages.fulfillmentPage.components.fulfillmentOrderCard.button.markDelivered">
                      {t("mark_delivered")}
                    </button>
                  )}

                  {isDelivery && canShowActionButtons && (
                    <button onClick={() => onSelectCourier(order)} className="w-full rounded-lg bg-orange-100 py-3 font-bold text-gray-800 ring-2 ring-orange-500 hover:bg-orange-400" data-interaction-description-key="interactionDescriptions:pages.fulfillmentPage.components.fulfillmentOrderCard.button.onSelectCourierOrder">
                      {t("select_courier")}
                    </button>
                  )}

                  {isDelivery && delivery && !canShowActionButtons && (
                    <RenderDeliveryDetails delivery={delivery} onCancelSearch={onCancelSearch} />
                  )}

                  {order.isStorePickup && (
                    <div className="rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-center text-xs font-bold text-emerald-800">
                      {t("pickup_order_no_courier")}
                    </div>
                  )}
                </div>
              )}
            </CanView>
          </div>
        </article>

        <OrderPrintOptionsPupop
          isOpen={isPrintOpen}
          title={t("title_print_target")}
          orderId={order.id}
          printScope="fulfillment"
          onClose={() => setIsPrintOpen(false)}
        />
      </>
    </Animate>
  )
}
